import { default as React, useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import GroupIcon from '@mui/icons-material/Group';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

interface Team {
  entityRef: string;
  name: string;
  displayName: string;
}

export const CatalogTeamPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);

  // Load catalog groups on mount
  useEffect(() => {
    const loadCatalogGroups = async () => {
      // eslint-disable-next-line no-console
      console.log('📚 CatalogTeamPicker: Loading catalog groups');

      try {
        setLoading(true);
        const groupEntities = await catalogApi.getEntities({
          filter: { kind: 'Group' },
        });

        const groupTeams: Team[] = groupEntities.items.map(entity => {
          const displayName =
            (entity.spec?.profile as { displayName?: string } | undefined)
              ?.displayName ||
            (entity.metadata.title as string) ||
            entity.metadata.name;

          return {
            entityRef: `group:default/${entity.metadata.name}`,
            name: entity.metadata.name,
            displayName,
          };
        });

        setTeams(groupTeams);
        // eslint-disable-next-line no-console
        console.log(`📚 CatalogTeamPicker: Loaded ${groupTeams.length} groups`);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading groups from catalog:', error);
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };

    loadCatalogGroups();
  }, [catalogApi]);

  // Initialize selected team from formData
  useEffect(() => {
    if (formData && typeof formData === 'string' && teams.length > 0) {
      const teamName = formData
        .replace('group:default/', '')
        .replace('user:default/', '');
      const team = teams.find(t => t.name === teamName);
      if (team) {
        setSelectedTeam(team);
      }
    }
  }, [formData, teams]);

  const handleTeamChange = (_event: any, newValue: Team | null) => {
    setSelectedTeam(newValue);
    if (!newValue) {
      onChange('');
      return;
    }
    onChange(newValue.entityRef);
  };

  const hasErrors = rawErrors && rawErrors.length > 0;

  return (
    <FormControl fullWidth className={classes.root} error={hasErrors}>
      <div className={classes.header}>
        <GroupIcon color="primary" />
        <Typography className={classes.title}>
          Team {required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
      </div>

      <Autocomplete
        options={teams}
        getOptionLabel={(option: Team) => {
          // Show displayName (name) if they're different, otherwise just displayName
          if (option.displayName !== option.name) {
            return `👥 ${option.displayName} (${option.name})`;
          }
          return `👥 ${option.displayName}`;
        }}
        onChange={handleTeamChange}
        value={selectedTeam}
        loading={loading}
        renderInput={(params: any) => (
          <TextField
            {...params}
            label="Select Team"
            variant="outlined"
            placeholder="Search for teams..."
            required={required}
            error={hasErrors}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText className={classes.errorText}>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}
      {!hasErrors && schema.description && (
        <FormHelperText className={classes.helperText}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const CatalogTeamPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'CatalogTeamPicker',
    component: CatalogTeamPicker,
  }),
);
